import React, { useRef, useState } from "react";
import SectionTitle from "../../components/Common/SectionTitle";
import banner_Img from "../../assets/img/banner/BlockchainIllustrationAura.svg";


import Logo from "../../assets/img/logo/metaqualt-logo1.webp";

import effectImg2 from "../../assets/img/about/dotted-3.webp";
import effectImg3 from "../../assets/img/about/shape3.webp";
import ConsultNowBtn from "../../components/ConsultNowBtn";

import { accordions } from "../../components/Accordian/Data";
import AccordionCards from "../../components/Accordian/AccordianCards";
import { faqDescriptions } from "../../components/Accordian/Data";
import Table from "./Table";
import { Shield, Search, TrendingUp } from "lucide-react";
import "./BlockChain_Main.css";
import CallToAction from "./CallToAction";

const blockchainExpertise = [
  {
    id: 1,
    title: "Enterprise Blockchain Solutions",
    gradient: "linear-gradient(135deg, #06b6d4, #2563eb)",
    tags: ["Private Networks", "Public Chains", "Security", "Scalability"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    id: 2,
    title: "Smart Contract Development",
    gradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
    tags: ["Ethereum", "Solana", "Hyperledger", "Automation"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    id: 3,
    title: "Decentralized Applications (dApps)",
    gradient: "linear-gradient(135deg, #10b981, #14b8a6)",
    tags: ["DeFi", "NFT Marketplace", "Web3", "UX"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
  {
    id: 4,
    title: "Blockchain Consulting & Strategy",
    gradient: "linear-gradient(135deg, #f97316, #ef4444)",
    tags: ["Tokenomics", "Security Audits", "Strategy", "Advisory"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
    ),
  },
  {
    id: 5,
    title: "Tokenization & Digital Assets",
    gradient: "linear-gradient(135deg, #3b82f6, #6366f1)",
    tags: ["Real Estate", "Art", "Finance", "Liquidity"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  {
    id: 6,
    title: "Gaming & NFTs with Governance & Identity",
    gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    tags: ["Gaming", "NFTs", "Governance", "Identity"],
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
  },
];

function ExpertiseCard({ item, index }) {
  return (
    <article className="es-card" style={{ animationDelay: `${index * 0.07}s` }}>
      <div className="es-glow" style={{ background: item.gradient }} />
      <div className="es-hover-fill" style={{ background: item.gradient }} />
      <div className="es-icon" style={{ background: item.gradient }}>
        {item.icon}
      </div>
      <div className="es-card-title">{item.title}</div>
      <div className="es-tags">
        {item.tags.map((t, i) => (
          <span key={i} className="es-tag">
            {t}
          </span>
        ))}
      </div>
    </article>
  );
}

const BlockChain_Main = () => {

  
  const processSteps = [
    {
      id: "01",
      title: "Business Discovery & Use Case Mapping",
      shortText:
        "Strategic assessment aligned with security, automation, and transparency goals.",
      description:
        "We begin by understanding your business ecosystem, technical requirements, and industry challenges to design a blockchain strategy tailored for long-term growth and operational efficiency.",
      bullets: [
        "Identify high-impact blockchain opportunities",
        "Define architecture and implementation roadmap",
        "Align business objectives with decentralized solutions",
      ],
      focus: "Business Analysis & Blockchain Strategy",
      deliverable: "Defined blockchain roadmap and implementation strategy",
      gradient: "linear-gradient(135deg, #06b6d4, #2563eb)",
      accent: "#38bdf8",
      icon: Search,
    },
    {
      id: "02",
      title: "Blockchain Architecture Planning",
      shortText:
        "Secure, scalable, and future-ready blockchain infrastructure planning.",
      description:
        "Our team designs the complete blockchain ecosystem including network structure, smart contracts, APIs, token logic, and security layers for seamless deployment.",
      bullets: [
        "Design decentralized system architecture",
        "Create secure smart contract frameworks",
        "Plan integrations with existing systems",
      ],
      focus: "Blockchain Architecture & System Design",
      deliverable: "Scalable decentralized infrastructure framework",
      gradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
      accent: "#a78bfa",
      icon: Shield,
    },
    {
      id: "03",
      title: "Blockchain Development & Implementation",
      shortText:
        "Enterprise-grade blockchain development with reliable execution.",
      description:
        "Our developers build secure blockchain applications with transparent logic, automation capabilities, and optimized transaction workflows.",
      bullets: [
        "Develop custom dApps and smart contracts",
        "Enable secure and automated transactions",
        "Build scalable blockchain platforms",
      ],
      focus: "Smart Contract & dApp Development",
      deliverable: "Secure blockchain applications and automation systems",
      gradient: "linear-gradient(135deg, #14b8a6, #22c55e)",
      accent: "#2dd4bf",
      icon: TrendingUp,
    },
    {
      id: "04",
      title: "Security Auditing & Deployment",
      shortText:
        "Risk-free deployment with advanced blockchain security validation.",
      description:
        "We ensure blockchain stability, transaction accuracy, scalability, and security compliance through rigorous testing and deployment procedures.",
      bullets: [
        "Perform smart contract audits and testing",
        "Reduce vulnerabilities and deployment risks",
        "Deploy secure blockchain environments",
      ],
      focus: "Security Validation & Infrastructure Deployment",
      deliverable: "Audited and securely deployed blockchain environment",
      gradient: "linear-gradient(135deg, #f97316, #ef4444)",
      accent: "#fb923c",
      icon: Shield,
    },
    {
      id: "05",
      title: "Maintenance & Continuous Optimization",
      shortText:
        "Continuous improvement for scalability, performance, and growth.",
      description:
        "Our team continuously monitors blockchain performance, upgrades infrastructure, and enhances system capabilities as your business evolves.",
      bullets: [
        "Monitor blockchain performance and stability",
        "Optimize scalability and transaction efficiency",
        "Deliver ongoing support and future enhancements",
      ],
      focus: "Performance Optimization & Ongoing Support",
      deliverable: "Enhanced scalability and continuous system improvement",
      gradient: "linear-gradient(135deg, #3b82f6, #6366f1)",
      accent: "#93c5fd",
      icon: TrendingUp,
    },
  ];

  const [activeProcessStep, setActiveProcessStep] = useState(0);

  const processPreviewRef = useRef(null);

  const currentProcessStep = processSteps[activeProcessStep];
  
  const ActiveProcessIcon = currentProcessStep.icon;

  const handleProcessStepChange = (index) => {
    setActiveProcessStep(index);

    if (typeof window !== "undefined" && window.innerWidth <= 991) {
      window.requestAnimationFrame(() => {
        processPreviewRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  const handleProcessStepHover = (index) => {
    if (typeof window !== "undefined" && window.innerWidth > 991) {
      setActiveProcessStep(index);
    }
  };

  return (
    <React.Fragment>
    
      <div className="rs-about pt-50 pb-50 md-pt-60">
        <div className="container">
          <div className="row pb-0">
            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12 pl-20">
              {/* Section Title Start */}
              <SectionTitle
                sectionClass="sec-title2 mb-30"
                subtitleClass="sub-text style-bg"
                subtitle="AI Innovation Hub"
                titleClass="title pb-25"
                title="Unlock the Power of Blockchain with MetaQualt"
                descClass="desc pb-5"
                description="Blockchain is more than just a technology; it’s a paradigm shift in trust, security, and efficiency. Since 2012, MetaQualt has been at the forefront of this revolution, crafting enterprise-grade blockchain solutions that redefine industries. From secure smart contracts to scalable decentralized applications (dApps), we enable businesses to harness blockchain’s full potential with precision, reliability, and innovation."
                secondDescClass="desc pb-16"
              />
              <ConsultNowBtn />
            </div>
            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12">
              <div className="text-center">
                <img
                  className="main"
                  src={banner_Img}
                  alt="MetaQualt blockchain illustration"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <div className="shape-image">
            <img className="top dance" src={effectImg2} alt="top dance" />
            <img className="bottom dance" src={effectImg3} alt="bottom dance" />
          </div>
        </div>
      </div>

      <div className="rs-process style6 pt-50 pb-50 gray-bg blockchain-page">
        <div className="container">
          <SectionTitle
            sectionClass="sec-title2 text-center mb-10"
            subtitleClass="sub-text style-bg"
            subtitle="OUR EXPERTISE"
            titleClass="title"
            title="Why Choose MetaQualt for Blockchain?"
            description="With over a decade of expertise, MetaQualt stands as a pioneer in blockchain innovation. Our approach blends deep industry knowledge, robust security protocols, and seamless integration capabilities."
            effectClass="heading-line"
          />

          <div className="es-wrap">
            <div className="es-grid" style={{ position: "relative" }}>
              <div className="es-column es-column-left">
                {blockchainExpertise.slice(0, 3).map((item, i) => (
                  <ExpertiseCard key={item.id} item={item} index={i} />
                ))}
              </div>

              <div className="es-center-visual">
                <div className="es-center-visual-inner">
                  <img src={Logo} alt="MetaQualt Blockchain Solutions" />
                </div>
              </div>

              <div className="es-column es-column-right">
                {blockchainExpertise.slice(3, 6).map((item, i) => (
                  <ExpertiseCard key={item.id} item={item} index={i + 3} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="rs-service" className="rs-services main-home style6 pt-50 pb-50">
        <div className="container">
          <SectionTitle
            sectionClass="sec-title2 text-center mb-46"
            subtitleClass="sub-text style-bg"
            subtitle="Process"
            titleClass="title title"
            title="Industries We Serve"
            effectClass="heading-line"
            descClass="desc max-w-3xl mx-auto px-4 md:px-0 text-center"
            description="From finance to healthcare, MetaQualt's blockchain solutions are reshaping industries worldwide. Our expertise spans:"
          />
          <div className="ai-process-shell">
            <div className="ai-process-steps">
              {processSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === activeProcessStep;

                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`ai-process-step ${isActive ? "is-active" : ""}`}
                    onClick={() => handleProcessStepChange(index)}
                    onMouseEnter={() => handleProcessStepHover(index)}
                    style={{ "--step-gradient": step.gradient }}
                  >
                    <span className="ai-process-step-marker">
                      <StepIcon size={18} />
                    </span>
                    <span className="ai-process-step-copy">
                      <span className="ai-process-step-number">{step.id}</span>
                      <span className="ai-process-step-title">
                        {step.title}
                      </span>
                      <span className="ai-process-step-text">
                        {step.shortText}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              ref={processPreviewRef}
              className="ai-process-preview"
              style={{
                "--process-gradient": currentProcessStep.gradient,
                "--process-accent": currentProcessStep.accent,
              }}
            >
              <div
                className="ai-process-preview-body"
                key={currentProcessStep.id}
              >
                <div className="ai-process-preview-top">
                  <span className="ai-process-preview-badge">
                    Step {currentProcessStep.id}
                  </span>
                  <span className="ai-process-preview-chip">
                    {currentProcessStep.focus}
                  </span>
                </div>

                <div className="ai-process-preview-hero">
                  <div className="ai-process-preview-orbit">
                    <div className="ai-process-preview-orbit-ring" />
                    <div className="ai-process-preview-icon">
                      <ActiveProcessIcon size={38} />
                    </div>
                    <div className="ai-process-float-card ai-process-float-card-one">
                      <span>Primary Focus</span>
                      <strong>{currentProcessStep.focus}</strong>
                    </div>
                    <div className="ai-process-float-card ai-process-float-card-two">
                      <span>Key Output</span>
                      <strong>{currentProcessStep.deliverable}</strong>
                    </div>
                  </div>

                  <div className="ai-process-preview-copy">
                    <div className="ai-process-preview-stepno">
                      {currentProcessStep.id}
                    </div>
                    <h3>{currentProcessStep.title}</h3>
                    <p>{currentProcessStep.description}</p>
                  </div>
                </div>

                <div className="ai-process-preview-grid">
                  {currentProcessStep.bullets.map((item, index) => (
                    <div key={index} className="ai-process-preview-point">
                      <span className="ai-process-preview-dot" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="rs-service" className="rs-services main-home style6 pt-50 pb-50">
        <div className="container">
          <SectionTitle
            sectionClass="sec-title2 text-center mb-46"
            subtitleClass="sub-text style-bg"
            subtitle="Technologies"
            titleClass="title title"
            title="Technologies We Use for Blockchain Solutions"
            effectClass="heading-line"
            descClass="desc w-80"
            description=""
          />
          <Table />
        </div>
      </div>

      <div className="accordions ai-faq-section pb-50 pt-50">
        <div className="container">
          <div className="ai-faq-shell">
            <div className="ai-faq-header">
              <span className="ai-faq-kicker">SUPPORT</span>
              <h2>Frequently Asked Questions</h2>
              <p>{faqDescriptions["Blockchain"].description}</p>
            </div>
            <AccordionCards data={accordions.Blockchain} />
          </div>
        </div>
      </div>

      <div className="expertise-section">
        <div className="l-shape1"></div>
        <div className="l-shape2"></div>
        <div className="l-shape3"></div>
        <div className="l-shape4"></div>
        <div className="mid-line-left"></div>
        <div className="mid-line-right"></div>
      </div>

      <CallToAction
        title="Future-Proof Your Business with MetaQualt Blockchain"
        descriptionOne="The blockchain revolution is happening now, and MetaQualt ensures you stay ahead. Whether you're a startup exploring blockchain or an enterprise ready for full-scale implementation, we have the expertise, technology, and vision to bring your ideas to life."
        descriptionTwo="Get in touch with our blockchain experts today and transform the way you do business."
        buttonText="Get In Touch"
        buttonLink="/contact-us"
      />
      
    </React.Fragment>
  );
};

export default BlockChain_Main;

